import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { CircleCheck } from 'lucide-react';
import { Button } from '@hqo/ui-atoms';
import { cn } from '@hqo/react-hooks';
import type { CheckInPhase } from 'app/shared/hooks/use-check-in-feedback';

interface CardEventActionsFooterProps {
  /** Whether self check-in is currently open — the row can render either action, or both. */
  canCheckIn: boolean;
  onCheckIn: () => void;
  /** Request in flight — the label swaps and the button locks so a double tap can't double-submit. */
  /**
   * The confirm beat after a successful check-in. `confirmed`/`leaving` turn the CTA into its own
   * confirmation; `done` means the caller should have stopped rendering the row's check-in at all.
   */
  checkInPhase: CheckInPhase;
  canCancel: boolean;
  onCancel: () => void;
}

/**
 * Action row for an upcoming event booking or resource booking: "Check in" as the primary pill
 * (matching "Book again"), with "Cancel" to its right in the destructive treatment the visitor footer
 * already uses. Either can be absent — the two windows open and close independently.
 *
 * A successful check-in is confirmed HERE, on the button, rather than in a dialog: the label becomes
 * "Checked in" over the tenant's primary one step darker, holds, and fades out. The alternative was a
 * modal the user had to dismiss to get back to a feed that looked exactly as it had before.
 */
export const CardEventActionsFooter = ({
  canCheckIn,
  onCheckIn,
  checkInPhase,
  canCancel,
  onCancel,
}: CardEventActionsFooterProps): JSX.Element => {
  const intl = useIntl();
  const isConfirmed = checkInPhase === 'confirmed' || checkInPhase === 'leaving';
  // The whole card is tappable, so a CTA must not also open the tile's deeplink.
  const handleCheckIn = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Confirmed is a statement, not an affordance. `pointer-events-none` already stops a tap, but a
      // keyboard Enter on a focused button is not a pointer event and would re-submit without this.
      if (isConfirmed) return;
      onCheckIn();
    },
    [isConfirmed, onCheckIn],
  );
  const handleCancel = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onCancel();
    },
    [onCancel],
  );

  // Two states, not three. There is deliberately no in-flight label: a "Checking in…" beat between
  // these put three different labels — and so three different pill widths — on screen inside about a
  // second, and its `disabled:opacity-50` lightened the button in the middle of the sequence. The
  // result read as a new button being built rather than one button confirming. The request is short
  // and a failure toasts, so the tap goes straight to confirmed.
  //
  // `activity.status.checkedIn` is the pill's own wording, reused rather than duplicated: the button is
  // saying the same thing the status will say once the projection catches up.
  const checkInLabelId = isConfirmed ? 'activity.status.checkedIn' : 'activity.action.checkIn';

  return (
    <div className="mt-3 flex items-center justify-end gap-2 border-t border-system-grey-light pt-3">
      {canCheckIn && (
        <Button
          type="button"
          onClick={handleCheckIn}
          // Never `disabled`. The Button's `disabled:opacity-50` is what made the sequence flicker,
          // and a double tap is already refused upstream — the handler returns early while the
          // mutation is pending — so nothing is protected by dimming it here.
          aria-disabled={isConfirmed || undefined}
          data-testid="activity-card-check-in"
          className={cn(
            'h-auto shrink-0 gap-1.5 rounded-full px-[15px] py-2 text-[13px] font-semibold shadow-none',
            // `check-in-confirmed` carries the derived tint (see src/index.css) — it is a plain class
            // rather than a `bg-[…]` utility so tailwind-merge cannot treat it as a conflict with the
            // Button's own `bg-primary` and drop one of the two.
            // Colour is transitioned, not swapped: the darkening is the confirmation, so it should be
            // seen happening. Opacity carries the exit.
            'transition-[background-color,opacity] duration-300',
            isConfirmed && 'check-in-confirmed pointer-events-none',
            checkInPhase === 'leaving' && 'opacity-0',
          )}
        >
          <CircleCheck size={15} strokeWidth={2} />
          {intl.formatMessage({ id: checkInLabelId })}
        </Button>
      )}
      {canCancel && (
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          data-testid="activity-card-cancel-event"
          className="h-auto shrink-0 rounded-full px-[15px] py-2 text-[13px] font-semibold text-system-pink shadow-none hover:bg-system-grey-light hover:text-system-pink"
        >
          {intl.formatMessage({ id: 'activity.action.cancel' })}
        </Button>
      )}
    </div>
  );
};
